import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import RecordVoiceOverRounded from '@mui/icons-material/RecordVoiceOverRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import VolumeUpRounded from '@mui/icons-material/VolumeUpRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import MiniTaskReplayButton from '../components/MiniTaskReplayButton';
import { GardenBackdrop } from '../components/RoundView';
import { withTurkishGenitive } from '../turkishSuffixes';
import '../sound-hunt/soundHunt.css';
import HandwritingPad from './HandwritingPad';
import LetterSlotsInput from './LetterSlotsInput';
import { normalizeTurkishLetters, supportsG12Freehand } from '../components/turkishLetterKeyboard';
import PencilBuddy from './PencilBuddy';
import OcrBenchmark from './OcrBenchmark';
import OcrSetup from './OcrSetup';
import { createPaddleRecognizer, MODEL_NAME } from './paddleOcr';
import { DEFAULT_THRESHOLDS, evaluateRecognition, evaluateHandwritingRecognition, PREVIEW_WORDS } from './previewConfig';
import { playRoundClip } from '../sound-hunt/roundClipPlayback';
import { PRACTICE_END_FEEDBACK_AUDIO, RETRY_FEEDBACK_AUDIO, RETRY_FEEDBACK_TEXT } from '../feedbackAudio';
import './duyVeYaz.css';

const FEEDBACK = {
  correct: ['Harika, duyduğun kelimeyi yazdın!', 'İstersen bir kelime daha dinleyelim.'],
  retry: ['Bir kez daha deneyelim!', 'Kelimeyi yeniden dinle, sonra tekrar yaz.'],
  uncertain: ['Yazını tam okuyamadım.', 'Biraz daha belirgin yazıp yeniden deneyebilirsin.'],
};
 
// Two G12 OCR verdicts, one encouraging voice and one child-facing text.
// Technical retry/uncertain decisions remain distinct for scoring/QA.
const G12_RETRY_AUDIO_ID = 'g12_shared_retry';
const G12_PRACTICE_END_AUDIO_ID = 'tr_g1_l2_g12_practice_end';
const G12_LETTER_RETRY_FEEDBACK = [RETRY_FEEDBACK_TEXT, 'Harfleri yeniden seçebilirsin.'];
const G12_RETRY_FEEDBACK = ['Yazını bu kez okuyamadım.', RETRY_FEEDBACK_TEXT];
const G12_PRACTICE_END = ['Bugün çok güzel çalıştın.', 'İstersen sonra yine birlikte deneriz!'];

// The prototype/laboratory and G12 share the same pad, ink, model and feedback.
// Student G12 accepts the server's explicit contract and always uses letter slots.
export default function HandwritingGame({ onExit, recognizerFactory = createPaddleRecognizer, round, classTitle, sharedAudioRef, localProgress, onComplete, initialInputMode = 'ink' }) {
  const dictation = Boolean(round);
  const words = dictation ? round.items.map(item => item.expected_word.normalize('NFC').trim().toLocaleUpperCase('tr-TR')) : PREVIEW_WORDS;
  const studentDictation = dictation && !round.preview && round.student_dictation === true
    && round.input_mode === 'letter_slots' && round.ocr_enabled === false;
  const freehandSupported = !dictation || (round.preview === true && supportsG12Freehand(round.items));
  const learnedLetters = dictation ? new Set(normalizeTurkishLetters(round.learned_letters)) : null;
  const validDictationTargets = !dictation || (words.length === 2 && round.items.every((item, i) =>
    words[i].length >= 1 && words[i].length <= 12
    && (!studentDictation || (words[i].length >= 2 && item.kind !== 'letter'))
    && [...words[i]].every(letter => learnedLetters.has(letter))
    && typeof item.id === 'string' && item.id.length > 0
    && typeof item.audio_id === 'string' && !!round.audio?.[item.audio_id]));
  const ownAudio = useRef(null);
  const audioRef = sharedAudioRef || ownAudio;
  const playback = useRef(null);
  const events = useRef([]);
  const finished = useRef(false);
  const advancedIndex = useRef(-1);
  const [requestedInputMode, setInputMode] = useState(initialInputMode === 'keys' || !freehandSupported ? 'keys' : 'ink');
  const inputMode = freehandSupported ? requestedInputMode : 'keys';
  useEffect(() => { if (!freehandSupported) setInputMode('keys'); }, [freehandSupported]);
  const [letters, setLetters] = useState('');
  const [heard, setHeard] = useState(false);
  const [instructionHeard, setInstructionHeard] = useState(false);
  const [feedbackFinished, setFeedbackFinished] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [index, setIndex] = useState(0);
  const [laboratory, setLaboratory] = useState(false);
  const [ink, setInk] = useState({ count: 0, drawing: false });
  const [phase, setPhase] = useState('idle');
  const [verdict, setVerdict] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [developer, setDeveloper] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [assisted, setAssisted] = useState(false);
  const [assistedFailures, setAssistedFailures] = useState(0);
  const [retryResetting, setRetryResetting] = useState(false);
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [speaking, setSpeaking] = useState(false);
  const [audioError, setAudioError] = useState('');
  const [ocrReady, setOcrReady] = useState(false);
  const [ocrWarmupError, setOcrWarmupError] = useState('');
  const [ocrProgress, setOcrProgress] = useState({ stage: 'library', percent: 0 });
  const [ocrSetupAttempt, setOcrSetupAttempt] = useState(0);
  const [successAudioError, setSuccessAudioError] = useState('');
  const [successReplay, setSuccessReplay] = useState(0);
  const [practiceEndAudioError, setPracticeEndAudioError] = useState('');
  const [practiceEndReplay, setPracticeEndReplay] = useState(0);
  const pad = useRef(null);
  const alive = useRef(false);
  const recognizer = useRef(null);
  const lock = useRef(false);
  const speech = useRef(null);
  const speechTimeout = useRef(null);
  const game = useRef(null);
  const advance = useRef(null);
  const nextWordAudio = useRef(null);
  const expected = words[index];
  const busy = phase === 'loading' || phase === 'recognizing';
  const celebrating = dictation && verdict === 'correct';
  const endingPractice = dictation && assisted && assistedFailures >= 2 && ['retry', 'uncertain'].includes(verdict);
  const locked = busy || celebrating || retryResetting || endingPractice;
  const listenRequired = dictation && !heard;
  const preparingInk = dictation && inputMode === 'ink' && !ocrReady;

  useEffect(() => {
    if (!celebrating) return undefined;
    // Once an immutable approved Tomris success clip is in the backend audio
    // contract, speak it completely *before* advancing to the next word.
    // Until then retain the visual success state, not a synthetic voice.
    const id = round?.success_audio_id;
    let timer, cancelled = false, clip = null;
    setSuccessAudioError('');
    const next = (delay) => { timer = setTimeout(() => { if (!cancelled) advance.current?.(); }, delay); };
    if (id && round.audio?.[id]) {
      clip = playRoundClip(round, audioRef, id);
      playback.current = clip;
      clip.done.then(() => {
        if (!cancelled) { playback.current = null; next(350); }
      }).catch(() => {
        if (!cancelled) { playback.current = null; setSuccessAudioError('Kutlama sesi çalınamadı. Yeniden dinleyelim.'); }
      });
    } else next(2000);
    return () => { cancelled = true; clearTimeout(timer); clip?.cancel(); };
  }, [celebrating, index, round, audioRef, successReplay]);

  // Retry and gentle practice ending use exact, explicitly approved recordings.
  // The summary cannot replace the practice-end clip before its actual end.
  useEffect(() => {
    if (!dictation || !['retry', 'uncertain'].includes(verdict)) return undefined;
    let active = true, timer, watchdog, completed = false;
    setRetryResetting(true); setFeedbackFinished(false);
    const finish = () => {
      if (!active || !alive.current || completed) return;
      completed = true; clearTimeout(watchdog);
      setSpeaking(false); setFeedbackFinished(true);
      timer = window.setTimeout(() => {
        if (active && alive.current) {
          if (!endingPractice) {
            if (inputMode === 'keys') setLetters('');
            else pad.current?.reset();
          }
          setRetryResetting(false);
        }
      }, 850);
    };
    if (!endingPractice) {
      const clip = playRoundClip({ ...round,
        audio: { ...round.audio, [G12_RETRY_AUDIO_ID]: RETRY_FEEDBACK_AUDIO } }, audioRef, G12_RETRY_AUDIO_ID);
      playback.current = clip; setSpeaking(true);
      const settle = () => {
        if (active && playback.current === clip) { playback.current = null; finish(); }
      };
      // Playback failure keeps the written encouragement and allows a retry;
      // never substitute a different device voice or accept the wrong answer.
      clip.done.then(settle, settle);
      watchdog = window.setTimeout(() => { clip.cancel(); finish(); }, 12000);
      return () => {
        active = false; clearTimeout(timer); clearTimeout(watchdog);
        if (playback.current === clip) playback.current = null;
        clip.cancel();
      };
    }
    setPracticeEndAudioError(''); setSpeaking(true);
    const clip = playRoundClip({ ...round,
      audio: { ...round.audio, [G12_PRACTICE_END_AUDIO_ID]: PRACTICE_END_FEEDBACK_AUDIO } }, audioRef, G12_PRACTICE_END_AUDIO_ID);
    playback.current = clip;
    clip.done.then(() => {
      if (active && playback.current === clip) { playback.current = null; finish(); }
    }).catch(() => {
      if (active && playback.current === clip) {
        playback.current = null; clearTimeout(watchdog); setSpeaking(false);
        setPracticeEndAudioError('Ses çalınamadı. Birlikte yeniden dinleyelim.');
      }
    });
    watchdog = window.setTimeout(() => clip.cancel(), 12000);
    return () => {
      active = false; clearTimeout(timer); clearTimeout(watchdog);
      if (playback.current === clip) playback.current = null;
      clip.cancel();
    };
  }, [dictation, verdict, index, endingPractice, inputMode, round, audioRef, practiceEndReplay]);

  // Two unsuccessful guided attempts end the whole practice without success or points.
  useEffect(() => {
    if (!endingPractice || !feedbackFinished) return undefined;
    const timer = window.setTimeout(() => {
      if (!alive.current || finished.current) return;
      events.current.push({ kind: 'end_practice', item_id: round.items[index].id });
      const progress = localProgress(round.game_id, events.current);
      if (progress.round_completed) {
        finished.current = true;
        playback.current?.cancel();
        playback.current = null;
        onComplete();
      }
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [endingPractice, feedbackFinished, index, round, localProgress, onComplete]);

  // Prepare the SDK, verified models and WASM before offering the ink pad.
  // Loading never traps the child: tabs and navigation remain available.
  useEffect(() => {
    if (!dictation || !round.preview || !round.preview_only || !validDictationTargets
      || inputMode !== 'ink' || !freehandSupported) return undefined;
    const engine = recognizerFactory();
    recognizer.current = engine;
    let active = true;
    setOcrReady(false); setOcrWarmupError('');
    setOcrProgress({ stage: 'library', percent: 0 });
    if (typeof engine.warmUp === 'function') {
      Promise.resolve().then(() => engine.warmUp(undefined, value => {
        if (active) setOcrProgress(value);
      })).then(() => {
        if (active) { setOcrReady(true); setOcrWarmupError(''); }
      }).catch(() => { if (active) setOcrWarmupError('Kalemimiz henüz hazır değil.'); });
    } else setOcrReady(true); // Unit-test and preview mock recognizers.
    return () => {
      active = false;
      void engine.dispose();
      if (recognizer.current === engine) recognizer.current = null;
    };
  }, [dictation, round?.preview, round?.preview_only, validDictationTargets, recognizerFactory, inputMode, freehandSupported, ocrSetupAttempt]);

  // The approved instruction must come from the backend's reviewed media map,
  // not a Google Drive URL or an unreviewed Web Speech fallback.
  function playInstruction() {
    if (!dictation || locked || speaking) return;
    const id = round.intro_audio_id;
    if (!id || !round.audio?.[id]) {
      setAudioError('Yönerge sesi henüz hazır değil. Tekrar deneyelim.');
      return;
    }
    stopSpeech(); setAudioError(''); setSpeaking(true); setHeard(false);
    const current = playRoundClip(round, audioRef, id);
    playback.current = current;
    current.done.then(() => {
      if (!alive.current || playback.current !== current) return;
      playback.current = null; setInstructionHeard(true);
      listenWord(); // first instruction, then approved target word
    }).catch(error => {
      if (alive.current && playback.current === current) {
        playback.current = null; setSpeaking(false); setAudioError('Tomris sesi çalınamadı. Ses düğmesine basıp tekrar deneyelim.');
      }
    });
  }

  function stopSpeech() {
    playback.current?.cancel(); playback.current = null;
    clearTimeout(speechTimeout.current);
    if (speech.current) {
      speech.current.onend = null; speech.current.onerror = null;
      window.speechSynthesis?.cancel(); speech.current = null;
    }
  }
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; stopSpeech(); if (!dictation) void recognizer.current?.dispose(); recognizer.current = null; };
  }, [dictation]);

  function listenWord() {
    if (!dictation) return;
    stopSpeech(); setAudioError(''); setSpeaking(false); setHeard(false);
    const current = playRoundClip(round, audioRef, round.items[index].audio_id);
    playback.current = current; setSpeaking(true);
    current.done.then(() => {
      if (alive.current && playback.current === current) {
        setSpeaking(false); setHeard(true); playback.current = null;
      }
    }).catch(error => {
      if (alive.current && playback.current === current) {
        setSpeaking(false); setHeard(false); setAudioError('Tomris sesi çalınamadı. Dinle düğmesiyle tekrar deneyelim.'); playback.current = null;
      }
    });
  }
  function listen() {
    if (dictation) {
      if (!instructionHeard) playInstruction();
      else listenWord();
      return;
    }
    stopSpeech(); setAudioError(''); setSpeaking(false);
    const synth = window.speechSynthesis;
    if (!synth || typeof window.SpeechSynthesisUtterance !== 'function') {
      setAudioError('Bu cihazda ses açılamadı. Ses destekleyen bir tarayıcıda yeniden deneyelim.'); return;
    }
    const voices = synth.getVoices().filter(voice => /^tr(?:-|$)/i.test(voice.lang));
    if (!voices.length) {
      setAudioError('Türkçe ses bulunamadı. Cihazına Türkçe ses ekleyip Dinle düğmesine yeniden bas.'); return;
    }
    const utterance = new window.SpeechSynthesisUtterance(expected.toLocaleLowerCase('tr-TR'));
    utterance.lang = 'tr-TR'; utterance.rate = 0.75;
    utterance.voice = voices.find(voice => voice.localService) || voices[0];
    speech.current = utterance;
    const finish = (error) => {
      if (speech.current !== utterance) return;
      clearTimeout(speechTimeout.current); speech.current = null;
      if (!alive.current) return;
      setSpeaking(false);
      if (error) setAudioError('Ses çalınamadı. Cihazın sesini kontrol edip yeniden Dinle düğmesine bas.');
    };
    utterance.onend = () => finish(false); utterance.onerror = () => finish(true);
    setSpeaking(true);
    speechTimeout.current = setTimeout(() => { finish(true); synth.cancel(); }, 15000);
    try { synth.speak(utterance); } catch (_) { finish(true); }
  }
  function changeWord(next) {
    if (lock.current || ink.drawing) return;
    stopSpeech(); setSpeaking(false); setAudioError('');
    pad.current?.reset(); setLetters(''); setIndex(next); setVerdict(null); setDiagnostic(null); setPhase('idle'); setHeard(false); setAttempted(false); setFeedbackFinished(false);
    setFailedAttempts(0); setAssisted(false); setAssistedFailures(0); setRetryResetting(false);
  }
  function changeInk(value) {
    setInk(value);
    if (value.drawing) { setVerdict(null); setDiagnostic(null); setPhase('idle'); }
  }
  function editInk(action) {
    if (inputMode === 'keys') setLetters('');
    else pad.current?.[action]();
    setVerdict(null); setDiagnostic(null); setPhase('idle');
  }
  // Default G12 tab: compare local keyboard letters without OCR. Record a
  // distinct event kind; letter entry must not be counted as OCR recognition.
  function changeLetters(value) {
    setLetters(value); setVerdict(null); setPhase('idle');
  }
  function submitLetters(value) {
    if (!dictation || inputMode !== 'keys' || locked || speaking || !heard
      || lock.current || finished.current || value.length !== expected.length) return;
    const decision = value === expected ? 'correct' : 'retry';
    setVerdict(decision); setAttempted(true); setPhase('done');
    if (decision !== 'correct') {
      if (assisted) setAssistedFailures(count => count + 1);
      else setFailedAttempts(count => count + 1);
    }
    events.current.push({ kind: 'letter_result', item_id: round.items[index].id,
      verdict: decision, assisted, value });
  }
  function switchInputMode(next) {
    if (!dictation || next === inputMode || (next === 'ink' && !freehandSupported)
      || busy || ink.drawing || finished.current) return;
    stopSpeech(); setSpeaking(false);
    // A mode change restarts the staff-only preview. Never mix OCR and keypad
    // attempts in one result or write student progress/points.
    events.current = []; advancedIndex.current = -1; finished.current = false;
    pad.current?.reset(); setLetters(''); setInk({ count: 0, drawing: false });
    setIndex(0); setHeard(false); setInstructionHeard(false); setAttempted(false);
    setVerdict(null); setDiagnostic(null); setPhase('idle'); setAudioError('');
    setFailedAttempts(0); setAssisted(false); setAssistedFailures(0);
    setFeedbackFinished(false); setRetryResetting(false); setOcrWarmupError('');
    setOcrReady(false); setSuccessAudioError(''); setPracticeEndAudioError(''); setPracticeEndReplay(0);
    setOcrProgress({ stage: 'library', percent: 0 });
    setInputMode(next);
  }
  function navigateInputMode(event) {
    const modes = freehandSupported ? ['keys', 'ink'] : ['keys'];
    if (modes.length === 1) return;
    let next;
    if (event.key === 'Home') next = 'keys';
    else if (event.key === 'End') next = 'ink';
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') next = modes[1 - modes.indexOf(inputMode)];
    else return;
    event.preventDefault();
    switchInputMode(next);
    event.currentTarget.querySelector(`#dyw-tab-${next}`)?.focus();
  }
  async function check() {
    if (inputMode === 'keys') return;
    if (preparingInk) return;
    if (lock.current || celebrating || speaking || !ink.count || ink.drawing || (dictation && !heard) || finished.current) return;
    lock.current = true; setVerdict(null); setDiagnostic(null);
    let image;
    try {
      const canvas = pad.current.createOcrInput();
      if (!canvas) return;
      image = canvas.toDataURL('image/png');
      setDiagnostic({ expected, image, model: MODEL_NAME });
      setPhase('loading');
      if (!recognizer.current) recognizer.current = recognizerFactory();
      const result = await recognizer.current.recognize(canvas, next => { if (alive.current) setPhase(next); });
      if (!alive.current) return;
      setOcrReady(true); setOcrWarmupError('');
      // The expected answer is compared ONLY after the blind OCR completes.
      setDiagnostic({ ...result, expected, image });
      const decision = (dictation ? evaluateHandwritingRecognition : evaluateRecognition)(expected, result.text, result.confidence, dictation ? DEFAULT_THRESHOLDS : thresholds);
      setVerdict(decision); setAttempted(true);
      if (dictation) {
        if (decision === 'retry' || decision === 'uncertain') {
          if (assisted) setAssistedFailures(value => value + 1);
          else setFailedAttempts(value => value + 1);
        }
        events.current.push({ kind: 'ocr_result', item_id: round.items[index].id, verdict: decision, assisted });
      }
      setPhase('done');
    } catch (error) {
      if (alive.current) {
        setPhase('error'); setAttempted(true); setOcrWarmupError('');
        if (dictation) events.current.push({ kind: 'ocr_result', item_id: round.items[index].id, verdict: 'error', assisted });
        setDiagnostic({ expected, image, model: MODEL_NAME, error: String(error?.message || error) });
      }
    } finally { lock.current = false; }
  }
  function continueDictation() {
    if (lock.current || (inputMode === 'ink' && ink.drawing) || !attempted || finished.current || !heard || audioError || advancedIndex.current === index) return;
    advancedIndex.current = index;
    events.current.push({ kind: 'continue', item_id: round.items[index].id, manual: verdict !== 'correct' });
    const progress = localProgress(round.game_id, events.current);
    if (progress.round_completed) { finished.current = true; stopSpeech(); onComplete(); }
    else changeWord(index + 1);
  }
  advance.current = continueDictation;
  nextWordAudio.current = listenWord;
  useEffect(() => {
    if (!dictation || !instructionHeard || index === 0) return undefined;
    const timer = window.setTimeout(() => nextWordAudio.current?.(), 0);
    return () => window.clearTimeout(timer);
  }, [dictation, index, instructionHeard]);
  if (dictation && (!(studentDictation || (round.preview && round.preview_only)) || round.assessment_mode !== 'handwriting_dictation'
    || !validDictationTargets)) {
    return <main className="dyw-preview-info"><h1>{round.preview ? 'Bu etkinlik yalnız yetkili öğretmen önizlemesinde açılabilir.' : 'Bu etkinlik şu anda açılamadı.'}</h1><button className="dyw-button" onClick={onExit}>{round.preview ? 'İçerik Değerlendirmeye dön' : 'Ana sayfa'}</button></main>;
  }
  const result = verdict && (dictation && ['retry', 'uncertain'].includes(verdict)
    ? inputMode === 'keys' ? G12_LETTER_RETRY_FEEDBACK : G12_RETRY_FEEDBACK
    : studentDictation && verdict === 'correct' ? ['Harika, duyduğunu yazdın!', 'Eline sağlık!'] : FEEDBACK[verdict]);
  if (laboratory) return <OcrBenchmark recognizerFactory={recognizerFactory} onExit={() => setLaboratory(false)} />;

  if (dictation) return <main className={`sound-hunt dyw-preview dyw-dictation${celebrating ? ' dyw-is-celebrating' : ''}${assisted ? ' dyw-is-assisted' : ''}`}>
    <GardenBackdrop />
    <Box className="sound-hunt-content dyw-g12-content">
      <MiniTaskGameHeader classTitle={classTitle || '1. Sınıf'} soundEnabled soundRequired />
      <Box className="sound-hunt-header">
        <Button className="sound-hunt-home" onClick={onExit} startIcon={<ArrowBackRounded />}>Ana sayfa</Button>
        <Typography component="h1" className="sound-hunt-title">
          {round.student?.display_name === 'Öğretmen' ? 'Benim Mini Görevim'
            : `${withTurkishGenitive(round.student?.display_name || 'Öğrenci')} Mini Görevi`}
        </Typography>
        <Box className="sound-hunt-progress" aria-label={`Yazma ${index + 1} / ${words.length}`}>
          <Typography sx={{ fontWeight: 900, fontSize: 'inherit', color: '#6d45df' }}>{index + 1} / {words.length}</Typography>
        </Box>
      </Box>
      <Box component="section" className="sound-hunt-bottom dyw-g12-board">
        <Typography className="sound-hunt-badge">✏️ Duy ve Yaz</Typography>
        <Box className={`dyw-mode-tabs${freehandSupported ? '' : ' dyw-single-mode'}`} role="tablist" aria-label="Yazma yöntemi" onKeyDown={navigateInputMode}
          sx={{ '& [role="tab"]': { fontFamily: theme => theme.typography.fontFamily } }}>
          <button type="button" role="tab" aria-selected={inputMode === 'keys'}
            id="dyw-tab-keys" aria-controls="dyw-input-panel" tabIndex={inputMode === 'keys' ? 0 : -1}
            className={inputMode === 'keys' ? 'is-selected' : ''}
            disabled={busy || ink.drawing} onClick={() => switchInputMode('keys')}>⌨️ Harflerle yaz</button>
          {freehandSupported && <button type="button" role="tab" aria-selected={inputMode === 'ink'}
            id="dyw-tab-ink" aria-controls="dyw-input-panel" tabIndex={inputMode === 'ink' ? 0 : -1}
            className={inputMode === 'ink' ? 'is-selected' : ''}
            disabled={busy || ink.drawing} onClick={() => switchInputMode('ink')}>✏️ Serbest yazma · Deneysel</button>}
        </Box>
        <Box className="dyw-g12-cue" aria-label="Görev adımları" role="group">
          <VolumeUpRounded /> Dinle <span aria-hidden="true">→</span> {inputMode === 'keys' ? '⌨️ Harfleri seç' : '✏️ Yaz'} <span aria-hidden="true">→</span> <CheckRounded /> Yazdım
        </Box>
        <Box className="dyw-g12-mission">
          <PencilBuddy happy={verdict === 'correct'} />
          {celebrating ? <div className="dyw-g12-cheer"><CheckRounded /><strong>{assisted ? 'Eline sağlık!' : 'Harika! Doğru yazdın!'}</strong></div>
            : <Typography component="h2" className="dyw-g12-instruction">{assisted ? 'Haydi birlikte yazalım!' : round.instruction}</Typography>}
          {!celebrating && <MiniTaskReplayButton label="Dinle"
            onClick={listenWord} disabled={locked || speaking || !instructionHeard} />}
        </Box>
        <Box className="dyw-g12-notebook-wrap" role="tabpanel" id="dyw-input-panel" aria-labelledby={`dyw-tab-${inputMode}`}>
          <div className="dyw-notebook">
            <div className="dyw-notebook-top">
              <span className="dyw-page-title" key={index}>
                {inputMode === 'keys' ? 'Harf kutularım' : 'Benim defterim'}
              </span>
            </div>
            {inputMode === 'keys' ? <LetterSlotsInput length={expected.length} alphabet={round.learned_letters}
              targetKind={round.items[index].kind}
              value={letters} onChange={changeLetters} onSubmit={submitLetters}
              disabled={locked || listenRequired || speaking} hint={assisted ? expected : ''}
              disabledMessage={!instructionHeard
                ? speaking ? 'Yönergeyi dinliyoruz…' : 'Önce yönergeyi dinle'
                : !heard ? speaking ? (studentDictation ? 'Birlikte dinliyoruz…' : 'Sözcüğü dinliyoruz…') : (studentDictation ? 'Önce dinle' : 'Önce sözcüğü dinle')
                  : 'Birazdan yeniden deneyelim.'}
              celebrating={celebrating} />
              : preparingInk ? <OcrSetup progress={ocrProgress} error={ocrWarmupError}
                onRetry={() => { setOcrWarmupError(''); setOcrSetupAttempt(value => value + 1); }}
                onUseLetters={() => { switchInputMode('keys'); document.getElementById('dyw-tab-keys')?.focus(); }} /> : <>
                <HandwritingPad ref={pad} disabled={locked || listenRequired} onChange={changeInk}
                  disabledMessage={!instructionHeard ? 'Önce yönergeyi dinle' : !heard ? 'Şimdi sözcüğü dinle' : ''}
                  exampleWord={celebrating || assisted ? expected : undefined} celebrating={celebrating} />
                <div className="dyw-actions">
                  <button className="dyw-button dyw-secondary" disabled={locked || listenRequired || !ink.count || ink.drawing} onClick={() => editInk('undo')}><UndoRounded />Geri Al</button>
                  <button className="dyw-button dyw-secondary" disabled={locked || listenRequired || !ink.count || ink.drawing} onClick={() => editInk('clear')}><DeleteOutlineRounded />Temizle</button>
                  <button className="dyw-button dyw-submit" disabled={locked || speaking || listenRequired || !ink.count || ink.drawing} onClick={check}>
                    {busy ? <span className="dyw-spinner" aria-hidden="true" /> : <CheckRounded />}
                    {busy ? 'Okunuyor…' : 'Yazdım'}
                  </button>
                </div>
              </>}
          </div>
        </Box>
        <Box className={`dyw-g12-feedback ${verdict || ''}${phase === 'idle' ? ' is-idle' : ''}`} id="dyw-pad-help" role="status" aria-live="polite" aria-atomic="true">
          {verdict === 'correct' && <span className="dyw-celebration" aria-hidden="true">{[0,1,2,3,4].map(star => <StarRounded key={star} style={{ '--star': star }} />)}</span>}
          {busy && <><HelpOutlineRounded /><div><strong>{phase === 'loading' ? 'Yazına hazırlanıyorum…' : 'Yazına bakıyorum…'}</strong><span>{phase === 'loading' ? 'Birazdan hazır olacak.' : 'Biraz bekleyelim.'}</span></div></>}
          {phase === 'error' && <><HelpOutlineRounded /><div><strong>Yazını şimdi okuyamadım.</strong><span>Yazın burada duruyor; biraz sonra tekrar deneyebilirsin.</span></div></>}
          {result && <>{verdict === 'correct' ? <CheckRounded /> : verdict === 'retry' ? <AutoAwesomeRounded /> : <HelpOutlineRounded />}
            <div><strong>{endingPractice ? G12_PRACTICE_END[0] : assisted && celebrating ? 'Birlikte başardık!' : result[0]}</strong>
              <span>{endingPractice ? G12_PRACTICE_END[1] : celebrating
                ? (index + 1 === words.length ? 'Defterini tamamladın!' : 'Birazdan yeni sözcüğü dinleyelim.') : result[1]}</span>
            </div></>}
          {phase === 'idle' && <span>{!instructionHeard ? 'Önce aşağıdaki Yönergeyi dinleyelim düğmesine bas.' : !heard ? 'Sözcüğü dinleyince yazmaya başlayabilirsin.' : inputMode === 'keys'
            ? assisted ? 'Açık renkli harflere bakarak sırayla seç.' : 'Harfleri sırayla seç, sonra Yazdım düğmesine bas.'
            : assisted ? 'Açık renkli sözcüğün üzerinden yaz, sonra Yazdım düğmesine bas.' : 'Sözcüğü dinle, çizgilerin arasına yaz.'}</span>}
        </Box>
        {successAudioError && <div className="dyw-g12-audio-retry"><p role="alert">{successAudioError}</p>
          <Button className="sound-hunt-control" onClick={() => setSuccessReplay(value => value + 1)}>Kutlamayı yeniden dinle</Button></div>}
        {practiceEndAudioError && <div className="dyw-g12-audio-retry"><p role="alert">{practiceEndAudioError}</p>
          <Button className="sound-hunt-control" disabled={speaking}
            onClick={() => setPracticeEndReplay(value => value + 1)}>Sesi yeniden dinle</Button></div>}
        {audioError && <Typography role="alert" className="sound-hunt-status">{audioError}</Typography>}
        {failedAttempts >= 3 && !assisted && !celebrating && <Button className="sound-hunt-control dyw-g12-help"
          disabled={locked || ink.drawing} startIcon={<HelpOutlineRounded />}
          onClick={() => { editInk('clear'); setAssisted(true); setAssistedFailures(0); }}>Birlikte yazalım</Button>}
        {assisted && !celebrating && !endingPractice && <Typography className="dyw-g12-attempts">Birlikte yazıyoruz · Kalan deneme: {Math.max(0, 2 - assistedFailures)}</Typography>}
        <Box className="sound-hunt-controls dyw-g12-audio-controls">
          <Button className="sound-hunt-control" startIcon={<RecordVoiceOverRounded />}
            disabled={locked || speaking} onClick={playInstruction}>Yönergeyi dinleyelim</Button>
        </Box>
      </Box>
    </Box>
  </main>;
  return <main className={`dyw-preview${dictation ? ' dyw-dictation' : ''}`}>
    {!dictation && <div className="dyw-admin-bar">
      <button className="dyw-back" onClick={onExit}><ArrowBackRounded fontSize="small" /> Mini Görevler</button>
      <span>Önizleme · Puan ve ilerleme kaydedilmez</span>
      <button className="dyw-back" disabled={busy || ink.drawing} onClick={() => { stopSpeech(); setSpeaking(false); editInk('clear'); setLaboratory(true); }}>OCR Test Laboratuvarı</button>
    </div>}
    <section className="dyw-garden" ref={game} aria-label="Duy ve Yaz önizlemesi">
      <GardenBackdrop />
      <div className="dyw-game-content">
        <MiniTaskGameHeader classTitle={classTitle || '1. Sınıf'} soundEnabled soundRequired />
        {dictation && <div className="dyw-task-head">
          <div className="dyw-task-nav">
            <button className="dyw-home" onClick={onExit}><ArrowBackRounded fontSize="small" /> Ana sayfa</button>
            <span className="dyw-task-progress" aria-label={`Sözcük ${index + 1} / ${words.length}`}>{index + 1} / {words.length}</span>
          </div>
          <h2 className="dyw-task-student">{round.student?.display_name && round.student.display_name !== 'Öğretmen'
            ? `${round.student.display_name}’in Mini Görevi` : 'Benim Mini Görevim'}</h2>
          <span className="dyw-task-title">✏️ Duy ve Yaz</span>
        </div>}
        {dictation && <div className="dyw-task-directions" aria-label="Görev adımları">
          <VolumeUpRounded fontSize="small" /> Dinle <span aria-hidden="true">→</span> ✏️ Yaz <span aria-hidden="true">→</span> ✓ Yazdım
        </div>}
        <div className="dyw-heading">
          <PencilBuddy happy={verdict === 'correct'} />
          <div className="dyw-mission">
          {!dictation && <span className="dyw-eyebrow">Duy ve Yaz <span aria-hidden="true">·</span> Sihirli yazı defterim</span>}
          <h1>{dictation ? round.instruction : 'Duyduğun kelimeyi yaz!'}</h1>
          </div>
          <button className={`dyw-listen dyw-button${speaking ? ' dyw-speaking' : ''}`} onClick={listen} disabled={locked} aria-label={speaking ? 'Yeniden dinle' : 'Dinle'}>
            <VolumeUpRounded /> Dinle
            <span className="dyw-sound-waves" aria-hidden="true"><i /><i /><i /></span>
          </button>
        </div>
        <div className="dyw-notebook">
          <div className="dyw-notebook-top"><span className="dyw-page-title" key={index}><span className="dyw-bookmark" aria-hidden="true"><StarRounded /></span>Benim defterim {!dictation && <span className="dyw-page-count">{index + 1} / {words.length}</span>}</span>
          </div>
          <HandwritingPad ref={pad} disabled={locked} onChange={changeInk} exampleWord={celebrating || assisted ? expected : undefined} celebrating={celebrating} />
          <div className="dyw-notebook-bottom"><span id="dyw-pad-help">Parmağınla, kaleminle veya farenle yaz.</span><span className="dyw-stickers" aria-hidden="true"><StarRounded /><i /><i /></span></div>
        <div className="dyw-actions">
          <button className="dyw-button dyw-secondary" disabled={locked || listenRequired || !ink.count || ink.drawing} onClick={() => editInk('undo')}><UndoRounded />Geri Al</button>
          <button className="dyw-button dyw-secondary" disabled={locked || listenRequired || !ink.count || ink.drawing} onClick={() => editInk('clear')}><DeleteOutlineRounded />Temizle</button>
          <button className="dyw-button dyw-submit" disabled={locked || !ink.count || ink.drawing || (dictation && !heard)} onClick={check}>
            {busy ? <span className="dyw-spinner" aria-hidden="true" /> : <CheckRounded />} {busy ? 'Okunuyor…' : 'Yazdım'}
          </button>
        </div>
        </div>
        <div className={`dyw-audio-status${audioError ? ' dyw-audio-error' : ''}`} role="status">{audioError || (speaking ? 'Kelimeyi dinle… Yazmaya devam edebilirsin.' : '')}</div>
        <div className={`dyw-feedback ${verdict || ''}`} role="status" aria-live="polite" aria-atomic="true">
          {verdict === 'correct' && <span className="dyw-celebration" aria-hidden="true">{[0, 1, 2, 3, 4].map(star => <StarRounded key={star} style={{ '--star': star }} />)}</span>}
          {busy && <div><strong>{phase === 'loading' ? 'Yazı okuyucusu hazırlanıyor…' : 'Yazına bakıyorum…'}</strong><span>{phase === 'loading' ? 'İlk açılış biraz sürebilir. Bağlantını açık tut.' : 'Biraz bekleyelim.'}</span></div>}
          {phase === 'error' && <><HelpOutlineRounded /><div><strong>Yazı okuyucusu açılamadı.</strong><span>Yazın burada duruyor. Bağlantını kontrol edip Yazdım düğmesine yeniden bas.</span></div></>}
          {result && <>{verdict === 'correct' ? <CheckRounded /> : verdict === 'retry' ? <AutoAwesomeRounded /> : <HelpOutlineRounded />}<div>
            <strong>{endingPractice ? G12_PRACTICE_END[0] : assisted && celebrating ? 'Birlikte başardık!' : result[0]}</strong>
            <span>{endingPractice ? G12_PRACTICE_END[1] : celebrating
              ? (index + 1 === words.length ? 'Defterini tamamladın!' : 'Birazdan yeni sözcüğü dinleyelim.') : result[1]}</span>
          </div></>}
          {phase === 'idle' && <span>{assisted ? 'Açık renkli sözcüğün üzerinden yaz, sonra “Yazdım” düğmesine bas.' : 'Hazır olduğunda “Yazdım” düğmesine bas.'}</span>}
        </div>
        {dictation ? <div className="dyw-help-slot">
          {failedAttempts >= 3 && !assisted && !celebrating && <button className="dyw-next dyw-button" disabled={locked || ink.drawing} onClick={() => { editInk('clear'); setAssisted(true); setAssistedFailures(0); }}><HelpOutlineRounded />Birlikte yazalım</button>}
          {assisted && !celebrating && !endingPractice && <p>Birlikte yazıyoruz · Kalan deneme: {Math.max(0, 2 - assistedFailures)}</p>}
        </div> : <button className="dyw-next dyw-button" disabled={busy || ink.drawing} onClick={() => changeWord((index + 1) % words.length)}>Başka kelime dinle →</button>}
      </div>
    </section>
    {!dictation && <aside className="dyw-preview-info">
      <p>Bu bir deneme alanıdır. Sonuçlar öğrenci puanı veya yazı kalitesi notu değildir.</p>
      <p>İlk “Yazdım” dokunuşunda SDK ve model dosyaları internetten indirilir (onlarca MB olabilir). Yazı görüntüsü tarayıcıda işlenir, dış servise gönderilmez. Dinleme için cihazın Türkçe sesi kullanılır; bazı sesler bağlantı gerektirebilir.</p>
      <button className="dyw-dev-toggle" aria-expanded={developer} aria-controls="dyw-developer" onClick={() => setDeveloper(value => !value)}>{developer ? 'Geliştirici panelini kapat' : 'Geliştirici panelini aç'}</button>
      {developer && <section id="dyw-developer" className="dyw-developer" aria-label="OCR geliştirici paneli">
        <h2>OCR tanı bilgileri · Yalnız önizleme</h2>
        {!dictation && <><label>Örnek kelime<select disabled={busy || ink.drawing} value={index} onChange={event => changeWord(Number(event.target.value))}>{PREVIEW_WORDS.map((word, i) => <option value={i} key={word}>{word}</option>)}</select></label>
        <div className="dyw-thresholds">{[['match', 'Doğru eşiği'], ['mismatch', 'Tekrar dene eşiği']].map(([key, label]) => <label key={key}>{label}<input type="number" min="0" max="1" step="0.05" value={thresholds[key]} disabled={busy} onChange={event => setThresholds(value => ({ ...value, [key]: Math.max(0, Math.min(1, Number(event.target.value))) }))} /></label>)}</div></>}
        <p>Güven skoru doğruluk garantisi değildir. Başlangıç eşikleri öğretmen onayına açıktır. Tek büyük yazı satırı kullanılır; mevcut okul glifleri değiştirilmedi.</p>
        <dl>{!dictation && <><dt>Beklenen metin</dt><dd>{expected}</dd></>}<dt>Ham tahmin</dt><dd>{diagnostic?.text ?? '—'}</dd><dt>Güven skoru (en düşük satır)</dt><dd>{diagnostic?.confidence == null ? '—' : diagnostic.confidence.toFixed(3)}</dd><dt>Tanıma süresi</dt><dd>{diagnostic?.latencyMs == null ? '—' : `${diagnostic.latencyMs} ms`}</dd><dt>Model</dt><dd>{diagnostic?.model || MODEL_NAME}</dd></dl>
        {diagnostic?.image && <figure><img src={diagnostic.image} alt="OCR'a verilen, yalnız öğrenci yazısını içeren beyaz zeminli görüntü" /><figcaption>Yalnız kalem izleri · defter çizgileri gönderilmez</figcaption></figure>}
        <pre>{diagnostic?.error || (diagnostic?.raw ? JSON.stringify(diagnostic.raw, null, 2) : 'Henüz tanıma yapılmadı.')}</pre>
      </section>}
    </aside>}
  </main>;
}
